import { Decimal } from '../../datastructures';
import { Nullable, Token } from '../../interfaces';
import nostraTokenBalancesAtom from './nostraTokenBalanceAtom';
import accountsAtom from './accountsAtom';
import { walletAddressAtom } from '../wallet';
import nostraTokenListAtom from './nostraTokenListAtom';
import assetTokenRatesAtom from './assetTokenRatesAtom';
import { getConfigManager } from '../../config';
import { createAsyncAtom } from '../../lib/state';
import { ASSETS } from '../../constants';

type PerTokenTotalIdle = {
  [token in Token]: Nullable<Decimal>;
};

interface PerTokenAccountTotalIdle {
  [walletAddress: string]: PerTokenTotalIdle;
}

interface AccountTotalIdleMap {
  [walletAddressAccountId: string]: Decimal;
}

const accountTotalIdleAtom = createAsyncAtom(async get => {
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);
  const accounts = await get(accountsAtom);
  const walletAddress = await get(walletAddressAtom);
  const nostraTokenList = await get(nostraTokenListAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);

  if (!walletAddress) {
    return null;
  }

  const configManager = getConfigManager();

  const perTokenAccountTotalIdleMap: PerTokenAccountTotalIdle = {};
  const accountTotalIdleMap: AccountTotalIdleMap = {};

  accounts.forEach((subAccountName, accountId) => {
    if (!subAccountName) {
      return;
    }

    nostraTokenList.forEach(nostraToken => {
      try {
        // Do not count non idle tokens when calculating total idle tokens for a account
        if (nostraToken.variant === 'debt' || nostraToken.variant.interestBearing) {
          return;
        }

        const accountTotalIdleKey = `${walletAddress}-${accountId}`;
        const tokenBalanceKey = `${walletAddress}-${accountId}-${nostraToken.address}`;
        const tokenRate =
          assetTokenRates.rate[configManager.getNostraTokenAssetAddress(nostraToken.address)] ?? Decimal.ONE;
        const tokenBalance = nostraTokenBalances[tokenBalanceKey];

        if (!perTokenAccountTotalIdleMap[walletAddress]) {
          perTokenAccountTotalIdleMap[walletAddress] = ASSETS.reduce(
            (map, asset) => ({ ...map, [asset]: null }),
            {} as PerTokenTotalIdle,
          );
        }

        if (!tokenBalance) {
          return;
        }

        perTokenAccountTotalIdleMap[walletAddress][nostraToken.underlyingToken] = (
          perTokenAccountTotalIdleMap[walletAddress][nostraToken.underlyingToken] ?? Decimal.ZERO
        ).add(tokenBalance);

        accountTotalIdleMap[accountTotalIdleKey] = (accountTotalIdleMap[accountTotalIdleKey] ?? Decimal.ZERO).add(
          tokenBalance.mul(tokenRate),
        );
      } catch (error) {
        console.error(`useAccountTotalIdle - error calculating total idle for ID ${accountId}`, error);
      }
    });
  });

  return {
    perToken: perTokenAccountTotalIdleMap,
    total: accountTotalIdleMap,
  };
});

export default accountTotalIdleAtom;
